import axios from 'axios'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  UserRound,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { Avatar } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AddressFields,
  AttachmentPreviewGrid,
  DatePicker,
  type AddressFieldsChange,
} from '@/components/shared'
import type { AttachmentRef } from '@/components/shared'
import {
  DEFAULT_COUNTRY,
  DOCUMENT_FILE_ACCEPT,
  isDocumentFileExtension,
} from '@/lib/constants'
import {
  SETTINGS_IMAGE_ACCEPT,
  SETTINGS_IMAGE_MAX_BYTES,
  validateSettingsImage,
} from '@/lib/settingsImageUpload'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import { avatarBackgroundFor } from '@/lib/avatarBackground'
import { renameLocalFile } from '@/lib/documentRename'
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  DEFAULT_WORKING_DAYS,
  TECHNICIAN_WEEK_DAYS_UI,
  getTechnicianRolePreset,
  technicianDetailToFormValues,
  toApiWorkingDays,
} from '@/lib/technicians'
import { isValidEmail } from '@/lib/validations/common'
import {
  formatPhoneInput,
  isValidPhoneNumber,
  normalizePhoneNumber,
} from '@/lib/phone'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import { getApiErrorMessage } from '@/services/api'
import { techniciansApi } from '@/services/technicians'
import {
  useCreateTechnician,
  useUpdateTechnician,
  useTechnician,
  useTechnicianSkillOptions,
} from '@/hooks/useTechnicians'
import type {
  ApiResponse,
  CreateTechnicianRequest,
  TechnicianCertification,
  TechnicianStatus,
} from '@/types'
import { TechnicianFormDrawerShell } from '@/components/technicians/TechnicianFormDrawerShell'
import TechniciansListPage from './TechniciansListPage'
import TechnicianDetailPage from './TechnicianDetailPage'

const MAX_EQUIPMENT = 10
const MAX_VEHICLES = 2

// Input caps (2.10.3 / 2.10.4 field spec). Text inputs truncate via maxLength;
// the two numeric ones cap their length in onChange, since maxLength has no
// effect on <input type="number">.
const MAX_CERTIFICATION_NAME = 100
const MAX_EQUIPMENT_NAME = 100
const MAX_VEHICLE_NAME = 100
const MAX_ZONE_LENGTH = 255
const MAX_RADIUS_DIGITS = 4
const MAX_COMMISSION_LENGTH = 6
const DEFAULT_BREAK_START = '12:00'
const EQUIPMENT_PRESETS = [
  'Pulvérisateur',
  'Nébulisateur',
  'Pistolet à gel',
  'Machine vapeur',
  'Échelle',
  'Perche télescopique',
  'Combinaison frelons',
] as const
const BREAK_DURATION_OPTIONS = ['none', '30', '60', '90', '120'] as const

type TechnicianFormMode = 'create' | 'edit'

interface SkillRow {
  name: string
  commission: string
}

interface VehicleRow {
  name: string
  plate: string
}

type RolePreset = 'commercial' | 'technician' | 'hybrid'

interface Errors {
  first_name?: string
  last_name?: string
  phone?: string
  phone2?: string
  email?: string
  country?: string
  city?: string
  postal_code?: string
  address?: string
  certibiocide_no?: string
  intervention_radius?: string
  working_days?: string
  start_time?: string
  end_time?: string
  break_start?: string
  break_end?: string
  leave_from?: string
  leave_to?: string
}

const FORM_STEPS = 3

const outlineBtnClass =
  'rounded-xl border-[#e7e9ef] bg-white px-4 py-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#f8f9fb]'

const primaryBtnClass =
  'rounded-xl bg-primary px-4 py-3 text-sm font-medium leading-none text-white hover:bg-primary/90'

const cancelBtnClass =
  'rounded-xl border border-[#e7e9ef] bg-white px-4 py-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#f8f9fb]'

const fieldInputClass =
  'h-10 rounded-xl border-transparent bg-[#f4f5f8] px-4 py-3 text-sm font-medium text-[#171a20] placeholder:font-normal placeholder:text-[#8f95a6] focus-visible:ring-0 focus-visible:ring-offset-0'

const nativeSelectClass = `${fieldInputClass} w-full`

const restyledControlTypographyClass =
  'text-sm font-normal leading-[1.4] placeholder:font-normal'

const restyledFieldInputClass = cn(
  fieldInputClass,
  restyledControlTypographyClass,
  FORM_CONTROL_FOCUS_CLASS
)

const restyledAddressControlClass = cn(
  'border-transparent',
  restyledControlTypographyClass,
  FORM_CONTROL_FOCUS_CLASS
)

const restyledAddressSelectClass = cn(
  'border-transparent text-sm font-normal leading-[1.4] data-[placeholder]:font-normal',
  FORM_CONTROL_SELECT_OPEN_CLASS
)

const restyledDatePickerClass = cn(
  restyledFieldInputClass,
  FORM_CONTROL_FOCUS_WITHIN_CLASS
)

const restyledNativeSelectClass = cn(
  nativeSelectClass,
  restyledControlTypographyClass,
  FORM_CONTROL_FOCUS_CLASS
)

const restyledSectionTitleClass =
  'normal-case font-heading text-sm font-semibold leading-[1.2] tracking-normal text-[#3d434e]'

const restyledFieldLabelClass = 'leading-4'

const addNewBtnClass =
  'rounded-xl border border-dashed border-[#ff5a1f]/50 px-4 py-3 text-xs font-medium leading-none text-primary hover:bg-[#fff5f1]'

const deleteBtnClass =
  'h-10 w-10 shrink-0 rounded-full p-3 text-destructive hover:bg-destructive/10'

function ensureVehicleRows(rows: VehicleRow[]): VehicleRow[] {
  const next = rows.slice(0, MAX_VEHICLES)
  while (next.length < MAX_VEHICLES) {
    next.push({ name: '', plate: '' })
  }
  return next
}

function parseTimeToMinutes(value: string): number | null {
  const [hours, minutes] = value.split(':').map(Number)
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }
  return hours * 60 + minutes
}

function minutesToTime(value: number): string {
  const hours = Math.floor(value / 60)
  const minutes = value % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function getBreakDurationValue(start: string, end: string): string {
  const startMinutes = parseTimeToMinutes(start)
  const endMinutes = parseTimeToMinutes(end)
  if (
    startMinutes === null ||
    endMinutes === null ||
    endMinutes <= startMinutes
  ) {
    return 'none'
  }
  return String(endMinutes - startMinutes)
}

function RequiredMark() {
  return (
    <span className='text-destructive' aria-hidden='true'>
      {' '}
      *
    </span>
  )
}

function IdentifySection({
  title,
  subtitle,
  children,
  titleClassName,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  titleClassName?: string
}) {
  return (
    <section className='space-y-4'>
      <div className='flex flex-wrap items-center gap-2 text-[14px] font-semibold uppercase tracking-[0] text-[#171a20]'>
        <span
          className='h-5 w-[3px] rounded-full bg-primary'
          aria-hidden='true'
        />
        <span className={cn('font-heading', titleClassName)}>{title}</span>
        {subtitle && (
          <span className={cn('font-heading text-[#171a20]', titleClassName)}>
            - {subtitle}
          </span>
        )}
      </div>
      {children}
    </section>
  )
}

function AccentSectionLabel({
  title,
  className,
}: {
  title: string
  className?: string
}) {
  return (
    <div className='flex items-center gap-3'>
      <span
        className='h-5 w-[3px] rounded-full bg-primary'
        aria-hidden='true'
      />
      <h2
        className={cn(
          'text-[14px] font-medium uppercase tracking-[0.12em] text-[#171a20]',
          className
        )}
      >
        {title}
      </h2>
    </div>
  )
}

function FieldLabel({
  htmlFor,
  children,
  className,
}: {
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className={cn('text-[12px] font-medium text-[#171a20]', className)}
    >
      {children}
    </Label>
  )
}

function RequiredLabel({
  htmlFor,
  children,
  className,
}: {
  htmlFor?: string
  children: ReactNode
  className?: string
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className={cn('text-[12px] font-medium text-[#171a20]', className)}
    >
      {children}
      <RequiredMark />
    </Label>
  )
}

function TechnicianFormContent({
  mode,
  onCancel,
  onSuccess,
}: {
  mode: TechnicianFormMode
  onCancel: () => void
  onSuccess: (technicianId: string) => void
}) {
  const { t } = useTranslation(['technicians', 'clients', 'shared'])
  const { id } = useParams<{ id: string }>()
  const isEdit = mode === 'edit'

  const { data: detail, isLoading } = useTechnician(isEdit ? id : undefined)
  const create = useCreateTechnician()
  const update = useUpdateTechnician(id ?? '')
  const { data: skillOptions } = useTechnicianSkillOptions()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [phone2, setPhone2] = useState('')
  const [email, setEmail] = useState('')
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY)
  const [city, setCity] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [address, setAddress] = useState('')
  const [certibiocideNo, setCertibiocideNo] = useState('')
  const [skills, setSkills] = useState<SkillRow[]>([])
  const [zones, setZones] = useState<string[]>([])
  const [zoneInput, setZoneInput] = useState('')
  const [isAddingZone, setIsAddingZone] = useState(false)
  const [equipmentInput, setEquipmentInput] = useState('')
  const [isVehicleEquipmentExpanded, setIsVehicleEquipmentExpanded] =
    useState(true)
  const [certifications, setCertifications] = useState<
    TechnicianCertification[]
  >([])
  const [equipment, setEquipment] = useState<string[]>([])
  const [vehicles, setVehicles] = useState<VehicleRow[]>(ensureVehicleRows([]))
  const [workingDays, setWorkingDays] = useState<string[]>([
    ...DEFAULT_WORKING_DAYS,
  ])
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME)
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME)
  const [breakStart, setBreakStart] = useState('')
  const [breakEnd, setBreakEnd] = useState('')
  const [hasBreak, setHasBreak] = useState(false)
  const [worksWeekends, setWorksWeekends] = useState(false)
  const [onLeave, setOnLeave] = useState(false)
  const [leaveFrom, setLeaveFrom] = useState('')
  const [leaveTo, setLeaveTo] = useState('')
  const [interventionRadius, setInterventionRadius] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  // Avatar (feedback #79): existing file id + a freshly selected local file.
  const [avatarFileId, setAvatarFileId] = useState<string | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)
  const [attachments, setAttachments] = useState<AttachmentRef[]>([])
  const [isDocumentDragActive, setIsDocumentDragActive] = useState(false)
  const [errors, setErrors] = useState<Errors>({})
  const [step, setStep] = useState(1)
  const [status, setStatus] = useState<TechnicianStatus>('active')
  const [rolePreset, setRolePreset] = useState<RolePreset>('hybrid')
  const initialStatusRef = useRef<TechnicianStatus>('active')
  const hydratedDocumentsForRef = useRef<string | null>(null)
  const avatarName = [firstName, lastName].filter(Boolean).join(' ').trim()

  const onAddressChange = (next: AddressFieldsChange) => {
    if (next.country !== undefined) setCountry(next.country)
    if (next.city !== undefined) setCity(next.city)
    if (next.postalCode !== undefined) setPostalCode(next.postalCode)
    if (next.address !== undefined) setAddress(next.address)
    if ('latitude' in next) {
      setLatitude(next.latitude != null ? String(next.latitude) : '')
    }
    if ('longitude' in next) {
      setLongitude(next.longitude != null ? String(next.longitude) : '')
    }
  }

  useEffect(() => {
    if (!isEdit || !detail?.data) return

    const values = technicianDetailToFormValues(detail.data)
    /* eslint-disable react-hooks/set-state-in-effect */
    setFirstName(values.firstName)
    setLastName(values.lastName)
    setPhone(values.phone)
    setPhone2(values.phone2)
    setEmail(values.email)
    setCountry(values.country)
    setCity(values.city)
    setPostalCode(values.postalCode)
    setAddress(values.address)
    setCertibiocideNo(values.certibiocideNo)
    setSkills(values.skills)
    setZones(values.zones)
    setZoneInput('')
    setIsAddingZone(false)
    setCertifications(values.certifications)
    setEquipment(values.equipment)
    setVehicles(ensureVehicleRows(values.vehicles))
    setWorkingDays(values.workingDays)
    setStartTime(values.startTime)
    setEndTime(values.endTime)
    setBreakStart(values.breakStart)
    setBreakEnd(values.breakEnd)
    setHasBreak(!!(values.breakStart && values.breakEnd))
    setWorksWeekends(values.worksWeekends)
    setOnLeave(values.onLeave)
    setLeaveFrom(values.leaveFrom)
    setLeaveTo(values.leaveTo)
    setInterventionRadius(values.interventionRadius)
    setLatitude(
      detail.data.latitude != null ? String(detail.data.latitude) : ''
    )
    setLongitude(
      detail.data.longitude != null ? String(detail.data.longitude) : ''
    )
    setAvatarFileId(detail.data.avatar_file_id ?? null)
    setAvatarUrl(detail.data.avatar_url ?? null)
    const nextStatus = detail.data.status ?? 'active'
    setStatus(nextStatus)
    setRolePreset(getTechnicianRolePreset(detail.data))
    initialStatusRef.current = nextStatus
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [isEdit, detail])

  useEffect(() => {
    if (!isEdit || !id || hydratedDocumentsForRef.current === id) {
      return
    }

    let active = true
    techniciansApi
      .documents(id, { limit: 100 })
      .then(response => {
        if (!active) return
        hydratedDocumentsForRef.current = id
        const persisted = (response.data ?? [])
          .filter(document => document.doc_type !== 'avatar')
          .map<AttachmentRef>(document => ({
            id: document.id,
            file_id: document.file_id,
            name: document.file_name,
            url: document.url,
            mime_type: document.file_type,
            size: document.file_size,
            created_at: document.created_at,
            source: 'upload',
          }))

        setAttachments(current => [
          ...persisted,
          ...current.filter(attachment => !!attachment.file),
        ])
      })
      .catch(error => {
        if (!active) return
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(error, t('clients:documents.error')),
        })
      })

    return () => {
      active = false
    }
  }, [id, isEdit, t])

  const toggleDay = (d: string) =>
    setWorkingDays(prev =>
      prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
    )

  const availableSkillNames = Array.from(
    new Set([
      ...(skillOptions ?? []),
      ...skills.map(skill => skill.name.trim()).filter(Boolean),
    ])
  )

  const toggleSkill = (name: string) => {
    setSkills(prev => {
      const existing = prev.find(skill => skill.name === name)
      if (existing) {
        return prev.filter(skill => skill.name !== name)
      }

      return [...prev, { name, commission: '5' }]
    })
  }

  const updateSkillCommission = (name: string, nextValue: string) => {
    setSkills(prev =>
      prev.map(skill =>
        skill.name === name
          ? {
              ...skill,
              commission: nextValue.slice(0, MAX_COMMISSION_LENGTH),
            }
          : skill
      )
    )
  }

  const breakDurationValue =
    hasBreak && breakStart && breakEnd
      ? getBreakDurationValue(breakStart, breakEnd)
      : 'none'

  const customEquipment = equipment.filter(
    item =>
      !EQUIPMENT_PRESETS.includes(item as (typeof EQUIPMENT_PRESETS)[number])
  )

  const toggleEquipment = (name: string) => {
    setEquipment(prev =>
      prev.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
    )
  }

  const addCustomEquipment = () => {
    const value = equipmentInput.trim()
    if (!value) return
    setEquipment(prev => (prev.includes(value) ? prev : [...prev, value]))
    setEquipmentInput('')
  }

  const updateVehicle = (
    index: number,
    field: keyof VehicleRow,
    value: string
  ) => {
    setVehicles(prev =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    )
  }

  const onBreakDurationChange = (nextValue: string) => {
    if (nextValue === 'none') {
      setHasBreak(false)
      setBreakStart('')
      setBreakEnd('')
      return
    }

    const minutes = Number(nextValue)
    if (!Number.isFinite(minutes) || minutes <= 0) return

    const start = breakStart || DEFAULT_BREAK_START
    const startMinutes = parseTimeToMinutes(start)
    if (startMinutes === null) return

    setHasBreak(true)
    setBreakStart(start)
    setBreakEnd(minutesToTime(startMinutes + minutes))
  }

  // Feedback #77 - add the typed zone as a badge, de-duplicated.
  const addZone = () => {
    const v = zoneInput.trim()
    if (!v) return
    setZones(prev => (prev.includes(v) ? prev : [...prev, v]))
    setZoneInput('')
    setIsAddingZone(false)
  }
  const removeZone = (z: string) => setZones(prev => prev.filter(x => x !== z))

  // Feedback #79 - stage the selected avatar and show a local preview.
  const onAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    const avatarError = validateSettingsImage(file)
    if (avatarError) {
      toast({
        variant: 'destructive',
        title:
          avatarError === 'type'
            ? t('detail.avatarUnsupportedFormat')
            : t('shared:attachFiles.tooLarge', {
                max: getFileSizeLimitMb(SETTINGS_IMAGE_MAX_BYTES),
              }),
      })
      return
    }
    setAvatarFile(file)
    setAvatarUrl(URL.createObjectURL(file))
  }

  const attachmentPreviews = useMemo(
    () =>
      attachments.map(attachment =>
        attachment.file
          ? {
              ...attachment,
              name: attachment.file.name,
              size: attachment.file.size,
              mime_type: attachment.file.type,
            }
          : attachment
      ),
    [attachments]
  )

  const queueDocumentFiles = (files: FileList | null) => {
    if (!files?.length) return

    const nextQueued: AttachmentRef[] = []

    for (const file of Array.from(files)) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!isDocumentFileExtension(ext)) {
        toast({
          variant: 'destructive',
          title: t('clients:documents.unsupportedFormat'),
        })
        continue
      }

      if (isFileTooLarge(file)) {
        toast({
          variant: 'destructive',
          title: t('shared:attachFiles.tooLarge', {
            max: getFileSizeLimitMb(),
          }),
        })
        continue
      }

      nextQueued.push({
        id: `pending-${file.name}-${file.size}-${file.lastModified}`,
        name: file.name,
        size: file.size,
        mime_type: file.type,
        source: 'upload',
        file,
      })
    }

    if (nextQueued.length === 0) return

    setAttachments(prev => {
      const seen = new Set(
        prev.map(
          attachment =>
            attachment.id ??
            `${attachment.name}-${attachment.size ?? 0}-${attachment.mime_type ?? ''}`
        )
      )
      const merged = [...prev]

      for (const attachment of nextQueued) {
        const key =
          attachment.id ??
          `${attachment.name}-${attachment.size ?? 0}-${attachment.mime_type ?? ''}`
        if (seen.has(key)) continue
        seen.add(key)
        merged.push(attachment)
      }

      return merged
    })
  }

  const removeAttachment = async (attachment: AttachmentRef) => {
    if (!attachment.id) return
    if (attachment.file || !isEdit || !id) {
      setAttachments(prev =>
        prev.filter(current => current.id !== attachment.id)
      )
      return
    }

    try {
      await techniciansApi.deleteDocument(id, attachment.id)
      setAttachments(prev =>
        prev.filter(current => current.id !== attachment.id)
      )
      toast({ variant: 'success', title: t('clients:documents.deleteSuccess') })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('clients:documents.deleteError')),
      })
    }
  }

  const renameAttachment = async (attachment: AttachmentRef, name: string) => {
    if (!attachment.id) return

    if (attachment.file) {
      const renamedFile = renameLocalFile(attachment.file, name)
      setAttachments(prev =>
        prev.map(current =>
          current.id === attachment.id
            ? {
                ...current,
                name: renamedFile.name,
                size: renamedFile.size,
                mime_type: renamedFile.type,
                file: renamedFile,
              }
            : current
        )
      )
      return
    }

    if (!isEdit || !id) return
    const response = await techniciansApi.renameDocument(
      id,
      attachment.id,
      name
    )
    setAttachments(prev =>
      prev.map(current =>
        current.id === attachment.id
          ? {
              ...current,
              name: response.data?.file_name ?? current.name,
              url: response.data?.url ?? current.url,
            }
          : current
      )
    )
    toast({ variant: 'success', title: t('clients:documents.renameSuccess') })
  }

  const validateStep1 = (): string | null => {
    const next: Errors = {}
    if (!firstName.trim()) next.first_name = t('form.validation.firstName')
    if (!lastName.trim()) next.last_name = t('form.validation.lastName')
    if (!phone.trim()) next.phone = t('form.validation.phone')
    else if (!isValidPhoneNumber(phone))
      next.phone = t('form.validation.phoneInvalid')
    if (phone2.trim() && !isValidPhoneNumber(phone2))
      next.phone2 = t('form.validation.phoneInvalid')
    if (!email.trim()) next.email = t('form.validation.email')
    if (!country.trim()) next.country = t('form.validation.country')
    if (!city.trim()) next.city = t('form.validation.city')
    if (!postalCode.trim()) next.postal_code = t('form.validation.postalCode')
    if (!address.trim()) next.address = t('form.validation.address')
    setErrors(next)
    if (Object.keys(next).length > 0) return Object.values(next)[0] ?? null

    if (email && !isValidEmail(email)) {
      return t('form.validation.emailInvalid')
    }
    return null
  }

  const validateStep2 = (): string | null => {
    const next: Pick<
      Errors,
      | 'working_days'
      | 'start_time'
      | 'end_time'
      | 'break_start'
      | 'break_end'
      | 'leave_from'
      | 'leave_to'
    > = {}

    if (workingDays.length === 0) {
      next.working_days = t('form.validation.workingDays')
    }
    if (!startTime.trim()) {
      next.start_time = t('form.validation.startTime')
    }
    if (!endTime.trim()) {
      next.end_time = t('form.validation.endTime')
    } else if (startTime.trim() && endTime <= startTime) {
      next.end_time = t('form.validation.endTimeAfterStart')
    }

    if (hasBreak) {
      if (!breakStart.trim()) {
        next.break_start = t('form.validation.breakStart')
      }
      if (!breakEnd.trim()) {
        next.break_end = t('form.validation.breakEnd')
      } else if (breakStart.trim() && breakEnd <= breakStart) {
        next.break_end = t('form.validation.breakEndAfterStart')
      }
    }

    if (onLeave) {
      if (!leaveFrom.trim()) {
        next.leave_from = t('form.validation.leaveFrom')
      }
      if (!leaveTo.trim()) {
        next.leave_to = t('form.validation.leaveTo')
      } else if (leaveFrom.trim() && leaveTo < leaveFrom) {
        next.leave_to = t('form.validation.leaveToBeforeFrom')
      }
    }

    setErrors(prev => ({
      ...prev,
      working_days: next.working_days,
      start_time: next.start_time,
      end_time: next.end_time,
      break_start: next.break_start,
      break_end: next.break_end,
      leave_from: next.leave_from,
      leave_to: next.leave_to,
    }))

    if (Object.keys(next).length > 0) {
      return Object.values(next)[0] ?? null
    }

    if (equipment.length > MAX_EQUIPMENT || vehicles.length > MAX_VEHICLES) {
      return t('form.validation.maxItems')
    }
    return null
  }

  const validateStep3 = (): string | null => {
    const next: Errors = {}
    if (!interventionRadius.trim() || Number(interventionRadius) <= 0) {
      next.intervention_radius = t('form.validation.interventionRadius')
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return Object.values(next)[0] ?? null
    return null
  }

  const validate = (): string | null => {
    const step1Error = validateStep1()
    if (step1Error) return step1Error
    const step2Error = validateStep2()
    if (step2Error) return step2Error
    return validateStep3()
  }

  const onSubmit = async () => {
    const error = validate()
    if (error) {
      toast({ variant: 'destructive', title: error })
      return
    }
    const selectedSkills = skills
      .filter(s => s.name.trim())
      .map(s => s.name.trim())
    const cleanCertifications = certifications
      .filter(c => c.name.trim())
      .map(c => ({
        name: c.name.trim(),
        valid_until: c.valid_until || undefined,
      }))
    const breakRange =
      hasBreak && breakStart && breakEnd
        ? `${breakStart}-${breakEnd}`
        : undefined
    const body: CreateTechnicianRequest = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      status: !isEdit ? status : undefined,
      email: email.trim() || undefined,
      role: rolePreset,
      phone: normalizePhoneNumber(phone) || undefined,
      phone_secondary: normalizePhoneNumber(phone2) || undefined,
      country: country.trim() || undefined,
      city: city.trim() || undefined,
      postal_code: postalCode.trim() || undefined,
      address: address.trim() || undefined,
      certibiocide_no: certibiocideNo.trim() || undefined,
      latitude: latitude.trim() ? Number(latitude) : undefined,
      longitude: longitude.trim() ? Number(longitude) : undefined,
      // Sent as null (not omitted) once the photo is removed, so the API clears
      // it instead of keeping the previous file.
      avatar_file_id: avatarFileId,
      skills: selectedSkills,
      zones,
      certifications: cleanCertifications,
      commission_rates: Object.fromEntries(
        skills
          .filter(s => s.name.trim() && s.commission.trim())
          .map(s => [s.name.trim(), Number(s.commission)])
      ),
      equipment: equipment
        .filter(name => name.trim())
        .map(name => ({ name: name.trim() })),
      vehicles: vehicles
        .filter(vehicle => vehicle.name.trim() || vehicle.plate.trim())
        .map(vehicle => ({
          name: vehicle.name.trim(),
          plate: vehicle.plate.trim() || undefined,
        })),
      works_weekends: worksWeekends,
      working_hours: {
        days: toApiWorkingDays(workingDays),
        start: startTime,
        end: endTime,
        break: breakRange,
      },
      on_leave: onLeave,
      leave_from: onLeave && leaveFrom ? leaveFrom : undefined,
      leave_to: onLeave && leaveTo ? leaveTo : undefined,
      intervention_radius_km: Number(interventionRadius),
    }
    try {
      // Save the technician first to obtain its id (needed for the id-scoped
      // file uploads used by both the avatar and the attached files).
      const saved = isEdit
        ? await update.mutateAsync(body)
        : await create.mutateAsync(body)
      const techId = saved.data?.id ?? id ?? ''

      // Feedback #79 - upload the freshly selected avatar, then persist its
      // file id back onto the technician.
      if (avatarFile && techId) {
        try {
          const up = await techniciansApi.uploadDocument(
            techId,
            avatarFile,
            'avatar'
          )
          const fileId = up.data?.file_id
          if (fileId) {
            await techniciansApi.update(techId, {
              ...body,
              avatar_file_id: fileId,
            })
          }
        } catch {
          toast({ variant: 'destructive', title: t('form.avatarUploadError') })
        }
      }

      // Feedback #75 - upload the staged attachments after the technician exists.
      const stagedFiles = attachments
        .filter(
          (attachment): attachment is AttachmentRef & { file: File } =>
            attachment.source === 'upload' && !!attachment.file
        )
        .map(attachment => attachment.file)

      if (stagedFiles.length > 0 && techId) {
        for (const file of stagedFiles) {
          try {
            await techniciansApi.uploadDocument(techId, file)
          } catch {
            toast({
              variant: 'destructive',
              title: t('form.attachUploadError', { name: file.name }),
            })
          }
        }
      }

      // Status changes use dedicated activate/deactivate endpoints (edit only).
      if (isEdit && techId && status !== initialStatusRef.current) {
        try {
          if (status === 'active') await techniciansApi.activate(techId)
          else await techniciansApi.deactivate(techId)
          initialStatusRef.current = status
        } catch {
          toast({
            variant: 'destructive',
            title:
              status === 'active'
                ? t('deactivateDialog.activateError')
                : t('deactivateDialog.deactivateError'),
          })
        }
      }

      toast({
        variant: 'success',
        title: isEdit ? t('form.updateSuccess') : t('form.createSuccess'),
      })
      onSuccess(techId)
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const apiErrors = (e.response?.data as ApiResponse<unknown> | undefined)
          ?.errors

        if (apiErrors) {
          const next: Errors = {}

          if (apiErrors.first_name?.length)
            next.first_name = apiErrors.first_name[0]
          if (apiErrors.last_name?.length)
            next.last_name = apiErrors.last_name[0]
          if (apiErrors.phone?.length) next.phone = apiErrors.phone[0]
          if (apiErrors.phone_secondary?.length)
            next.phone2 = apiErrors.phone_secondary[0]
          if (apiErrors.email?.length) next.email = apiErrors.email[0]
          if (apiErrors.country?.length) next.country = apiErrors.country[0]
          if (apiErrors.city?.length) next.city = apiErrors.city[0]
          if (apiErrors.postal_code?.length) {
            next.postal_code = apiErrors.postal_code[0]
          }
          if (apiErrors.address?.length) next.address = apiErrors.address[0]
          if (apiErrors.certibiocide_no?.length) {
            next.certibiocide_no = apiErrors.certibiocide_no[0]
          }
          if (apiErrors.leave_from?.length) {
            next.leave_from = apiErrors.leave_from[0]
          }
          if (apiErrors.leave_to?.length) next.leave_to = apiErrors.leave_to[0]
          if (apiErrors.intervention_radius_km?.length) {
            next.intervention_radius = apiErrors.intervention_radius_km[0]
          }

          if (Object.keys(next).length > 0) {
            setErrors(prev => ({ ...prev, ...next }))

            if (
              next.first_name ||
              next.last_name ||
              next.phone ||
              next.phone2 ||
              next.email ||
              next.country ||
              next.city ||
              next.postal_code ||
              next.address ||
              next.certibiocide_no
            ) {
              setStep(1)
            } else if (next.leave_from || next.leave_to) {
              setStep(2)
            } else if (next.intervention_radius) {
              setStep(3)
            }
          }
        }
      }

      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          isEdit ? t('form.updateError') : t('form.createError')
        ),
      })
    }
  }

  const closeForm = () => {
    onCancel()
  }

  const goBack = () => {
    if (step === 1) closeForm()
    else setStep(step - 1)
  }

  const goNext = () => {
    if (step === 1) {
      const error = validateStep1()
      if (error) {
        toast({ variant: 'destructive', title: error })
        return
      }
      setStep(2)
      return
    }
    if (step === 2) {
      const error = validateStep2()
      if (error) {
        toast({ variant: 'destructive', title: error })
        return
      }
      setStep(3)
      return
    }
    void onSubmit()
  }

  const pageTitle = isEdit ? t('form.editTitle') : t('form.createTitle')
  const steps = [
    t('form.stepIdentify'),
    t('form.stepPlanning'),
    t('form.stepSector'),
  ]

  if (isEdit && isLoading) {
    return (
      <div className='flex h-full flex-col bg-[#fafafa]'>
        <header className='border-b border-border bg-background px-6 py-4'>
          <Skeleton className='h-8 w-48' />
        </header>
        <div className='flex-1 px-6 py-6'>
          <Skeleton className='h-96 w-full rounded-2xl' />
        </div>
      </div>
    )
  }

  const pending = create.isPending || update.isPending
  const roleCards = [
    {
      id: 'commercial' as const,
      icon: BriefcaseBusiness,
      title: t('form.rolePresets.commercial.title'),
      description: t('form.rolePresets.commercial.description'),
      tag: t('form.rolePresets.commercial.tag'),
    },
    {
      id: 'technician' as const,
      icon: UserRound,
      title: t('form.rolePresets.technician.title'),
      description: t('form.rolePresets.technician.description'),
      tag: t('form.rolePresets.technician.tag'),
    },
    {
      id: 'hybrid' as const,
      icon: RefreshCw,
      title: t('form.rolePresets.hybrid.title'),
      description: t('form.rolePresets.hybrid.description'),
      tag: t('form.rolePresets.hybrid.tag'),
    },
  ]
  const weekdayLabels: Record<
    (typeof TECHNICIAN_WEEK_DAYS_UI)[number],
    string
  > = {
    Mon: t('availability.days.mon'),
    Tue: t('availability.days.tue'),
    Wed: t('availability.days.wed'),
    Thu: t('availability.days.thu'),
    Fri: t('availability.days.fri'),
    Sat: t('availability.days.sat'),
    Sun: t('availability.days.sun'),
  }
  const breakDurationOptions = BREAK_DURATION_OPTIONS.map(value => ({
    value,
    label: t(`form.breakOptions.${value}`),
  }))

  return (
    <div className='flex h-full flex-col bg-white'>
      <div className='sticky top-0 z-20 bg-white'>
        <header className='tf-right-drawer-header tf-right-drawer-header-accent'>
          <h1 className='min-w-0 shrink-0 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
            {pageTitle}
          </h1>
          <div className='flex min-w-0 flex-1 items-center gap-2 whitespace-nowrap text-xs font-medium leading-4 text-[#3d434e]'>
            <span>
              {t('form.stepCounterShort', {
                current: step,
                total: FORM_STEPS,
              })}
            </span>
            <span
              className='h-1 w-1 shrink-0 rounded-full bg-[#8f95a6]'
              aria-hidden='true'
            />
            <span className='truncate'>{steps[step - 1]}</span>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='icon'
            className='size-9 shrink-0 rounded-xl bg-[#f4f5f8] text-[#5f6673] hover:bg-[#f4f5f8]'
            onClick={closeForm}
            disabled={pending}
          >
            <X className='size-4' />
          </Button>
        </header>

        <div className='px-6 p-4'>
          <div className='rounded-2xl border border-[#e7e9ef] bg-white p-2 shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
            <div className='flex items-center gap-2'>
              {steps.map((label, idx) => {
                const n = idx + 1
                const active = step === n
                const done = step > n

                return (
                  <div key={label} className='contents'>
                    <button
                      type='button'
                      className={cn(
                        'flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium leading-[1.4] transition-colors',
                        active && 'bg-[#ff5a1f] text-white',
                        done && !active && 'bg-[#ecfdf5] text-[#047857]',
                        !active && !done && 'text-[#6b7280]'
                      )}
                      onClick={() => {
                        if (n < step) setStep(n)
                      }}
                      disabled={pending || n > step}
                    >
                      <span
                        className={cn(
                          'flex h-6 w-6 items-center justify-center rounded-full border text-xs font-medium',
                          active && 'border-white/40 bg-white/20 text-white',
                          done &&
                            !active &&
                            'border-[#10b981] bg-[#10b981] text-white',
                          !active &&
                            !done &&
                            'border-[#d8dde8] bg-white text-[#8f95a6]'
                        )}
                      >
                        {done ? <Check className='h-3 w-3' /> : n}
                      </span>
                      <span className='truncate'>{label}</span>
                    </button>
                    {idx < steps.length - 1 && (
                      <span
                        className='h-0.5 w-4 shrink-0 rounded-full bg-[#e7e9ef]'
                        aria-hidden='true'
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto px-6 py-6 pb-32'>
        <div className='flex flex-col gap-6'>
          {step === 1 && (
            <>
              <IdentifySection
                title={t('form.informationSection')}
                titleClassName={restyledSectionTitleClass}
              >
                <div className='space-y-4 rounded-2xl bg-white'>
                  <div className='space-y-4'>
                    <div className='space-y-1'>
                      <FieldLabel className={restyledFieldLabelClass}>
                        {t('form.avatar')}
                      </FieldLabel>
                      <div className='flex items-center gap-3'>
                        <input
                          ref={avatarInputRef}
                          type='file'
                          accept={SETTINGS_IMAGE_ACCEPT}
                          className='hidden'
                          onChange={onAvatarSelect}
                        />
                        <button
                          type='button'
                          onClick={() => avatarInputRef.current?.click()}
                          className={cn(
                            'flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#e5e7ee] bg-[#f4f5f8] text-[#3d434e]',
                            avatarUrl &&
                              'border-transparent bg-primary text-white'
                          )}
                          aria-label={t('form.uploadAvatar')}
                        >
                          <Avatar
                            name={avatarName || null}
                            src={avatarUrl}
                            className='h-full w-full bg-transparent font-heading text-2xl font-semibold leading-[1.2] tracking-[-0.48px] text-white'
                            style={
                              avatarUrl
                                ? undefined
                                : {
                                    background: avatarBackgroundFor(avatarName),
                                  }
                            }
                          />
                        </button>
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-center gap-3'>
                            <button
                              type='button'
                              onClick={() => avatarInputRef.current?.click()}
                              className='inline-flex h-10 items-center gap-2 rounded-xl border border-dashed border-[#ff8a61] bg-white px-3 text-sm font-medium leading-none text-primary'
                            >
                              <ImagePlus className='h-4 w-4' />
                              {t('form.addPhoto')}
                            </button>
                            {avatarUrl && (
                              <Button
                                type='button'
                                variant='outline'
                                className='h-10 rounded-xl border-[#b91c1c] px-3 text-sm font-medium leading-none text-[#991b1b] shadow-none hover:bg-[#fef2f2] hover:text-[#991b1b]'
                                onClick={() => {
                                  setAvatarFile(null)
                                  setAvatarFileId(null)
                                  setAvatarUrl(null)
                                }}
                              >
                                <Trash2 className='mr-2 h-4 w-4' />
                                {t('form.removeAvatar')}
                              </Button>
                            )}
                          </div>
                          <p className='mt-1 text-xs font-medium leading-4 text-[#6b7280]'>
                            {t('form.avatarHint')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className='space-y-1'>
                      <FieldLabel className={restyledFieldLabelClass}>
                        {t('form.status')}
                      </FieldLabel>
                      <div className='flex flex-wrap gap-2'>
                        {(['active', 'inactive'] as TechnicianStatus[]).map(
                          value => (
                            <button
                              key={value}
                              type='button'
                              className={cn(
                                'rounded-xl border px-4 py-2 text-xs font-normal leading-4 transition-colors',
                                status === value
                                  ? 'border-[#059669] bg-[#ecfdf5] text-[#171a20]'
                                  : 'border-[#e5e7ee] bg-white text-[#171a20] hover:bg-[#f8f9fb]'
                              )}
                              onClick={() => setStatus(value)}
                            >
                              {t(`statusOptions.${value}`)}
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-1'>
                      <RequiredLabel
                        htmlFor='first_name'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.firstName')}
                      </RequiredLabel>
                      <Input
                        id='first_name'
                        value={firstName}
                        maxLength={80}
                        placeholder={t('clients:form.enterHere')}
                        className={cn(
                          restyledFieldInputClass,
                          errors.first_name && 'border-destructive'
                        )}
                        onChange={e => setFirstName(e.target.value)}
                      />
                      {errors.first_name && (
                        <p className='text-xs text-destructive'>
                          {errors.first_name}
                        </p>
                      )}
                    </div>
                    <div className='space-y-1'>
                      <RequiredLabel
                        htmlFor='last_name'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.lastName')}
                      </RequiredLabel>
                      <Input
                        id='last_name'
                        value={lastName}
                        maxLength={80}
                        placeholder={t('clients:form.enterHere')}
                        className={cn(
                          restyledFieldInputClass,
                          errors.last_name && 'border-destructive'
                        )}
                        onChange={e => setLastName(e.target.value)}
                      />
                      {errors.last_name && (
                        <p className='text-xs text-destructive'>
                          {errors.last_name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-1'>
                      <RequiredLabel
                        htmlFor='phone'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.phone1')}
                      </RequiredLabel>
                      <Input
                        id='phone'
                        value={phone}
                        type='tel'
                        maxLength={24}
                        placeholder={t('clients:form.enterHere')}
                        className={cn(
                          restyledFieldInputClass,
                          errors.phone && 'border-destructive'
                        )}
                        onChange={e =>
                          setPhone(formatPhoneInput(e.target.value))
                        }
                      />
                      {errors.phone && (
                        <p className='text-xs text-destructive'>
                          {errors.phone}
                        </p>
                      )}
                    </div>
                    <div className='space-y-1'>
                      <FieldLabel
                        htmlFor='phone2'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.phone2')}
                      </FieldLabel>
                      <Input
                        id='phone2'
                        value={phone2}
                        type='tel'
                        maxLength={24}
                        placeholder={t('clients:form.enterHere')}
                        className={cn(
                          restyledFieldInputClass,
                          errors.phone2 && 'border-destructive'
                        )}
                        onChange={e =>
                          setPhone2(formatPhoneInput(e.target.value))
                        }
                      />
                      {errors.phone2 && (
                        <p className='text-xs text-destructive'>
                          {errors.phone2}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='space-y-1'>
                    <RequiredLabel
                      htmlFor='email'
                      className={restyledFieldLabelClass}
                    >
                      {t('form.email')}
                    </RequiredLabel>
                    <Input
                      id='email'
                      type='email'
                      value={email}
                      maxLength={100}
                      placeholder={t('clients:form.enterHere')}
                      className={cn(
                        restyledFieldInputClass,
                        errors.email && 'border-destructive'
                      )}
                      onChange={e => setEmail(e.target.value)}
                    />
                    {errors.email && (
                      <p className='text-xs text-destructive'>{errors.email}</p>
                    )}
                  </div>

                  <AddressFields
                    layout='stacked'
                    country={country}
                    city={city}
                    postalCode={postalCode}
                    address={address}
                    onChange={onAddressChange}
                    translationNs='technicians'
                    formatCountryLabel={c => t(`clients:form.countries.${c}`)}
                    required
                    controlClassName={restyledAddressControlClass}
                    selectControlClassName={restyledAddressSelectClass}
                    errors={{
                      city: errors.city,
                      address: errors.address,
                      postalCode: errors.postal_code,
                      country: errors.country,
                    }}
                    countryTrailingSlot={
                      <div className='space-y-1'>
                        <Label
                          htmlFor='certibiocide_no'
                          className='text-xs font-medium leading-4 text-[#171a20]'
                        >
                          {t('form.certibiocideNo')}
                        </Label>
                        <Input
                          id='certibiocide_no'
                          value={certibiocideNo}
                          maxLength={100}
                          placeholder={t('clients:form.enterHere')}
                          className={cn(
                            restyledFieldInputClass,
                            errors.certibiocide_no && 'border-destructive'
                          )}
                          onChange={e => {
                            setCertibiocideNo(e.target.value)
                            if (errors.certibiocide_no) {
                              setErrors(prev => ({
                                ...prev,
                                certibiocide_no: undefined,
                              }))
                            }
                          }}
                        />
                        {errors.certibiocide_no && (
                          <p className='text-xs text-destructive'>
                            {errors.certibiocide_no}
                          </p>
                        )}
                      </div>
                    }
                  />
                </div>
              </IdentifySection>

              <IdentifySection
                title={t('form.roleSection')}
                subtitle={t('form.roleSectionSubtitle')}
                titleClassName={restyledSectionTitleClass}
              >
                <div className='grid gap-4 md:grid-cols-3'>
                  {roleCards.map(card => {
                    const Icon = card.icon
                    const selected = rolePreset === card.id

                    return (
                      <button
                        key={card.id}
                        type='button'
                        className={cn(
                          'rounded-2xl border p-4 text-left transition-colors',
                          selected
                            ? 'border-[#6ee7b7] bg-[#ecfdf3] shadow-sm'
                            : 'border-[#e7e9ef] bg-white hover:border-[#d8dde8]'
                        )}
                        onClick={() => setRolePreset(card.id)}
                      >
                        <div
                          className={cn(
                            'mb-4 flex h-10 w-10 items-center justify-center rounded-xl',
                            selected
                              ? 'bg-[#12b76a] text-white'
                              : 'bg-[#f4f5f8] text-[#4b5565]'
                          )}
                        >
                          <Icon className='h-5 w-5' />
                        </div>
                        <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                          {card.title}
                        </p>
                        <p className='mt-1 text-xs font-medium leading-4 text-[#8f95a6]'>
                          {card.description}
                        </p>
                        <div
                          className={cn(
                            'mt-4 inline-flex rounded-xl px-2.5 py-1.5 text-xs font-medium leading-4 text-[#171a20]',
                            selected
                              ? 'bg-white shadow-[0_1px_2px_rgba(23,26,32,0.08)]'
                              : 'bg-[#f2f4f7]'
                          )}
                        >
                          {card.tag}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </IdentifySection>

              <IdentifySection
                title={t('form.certificationsSection')}
                titleClassName={restyledSectionTitleClass}
              >
                <div className='space-y-3'>
                  {certifications.map((c, i) => (
                    <div
                      key={i}
                      className='flex flex-col gap-3 md:flex-row md:items-end'
                    >
                      <div className='min-w-0 flex-1 space-y-1'>
                        <FieldLabel className={restyledFieldLabelClass}>
                          {t('form.certificationName')}
                        </FieldLabel>
                        <Input
                          value={c.name}
                          maxLength={MAX_CERTIFICATION_NAME}
                          placeholder={t('clients:form.enterHere')}
                          className={restyledFieldInputClass}
                          onChange={e =>
                            setCertifications(prev =>
                              prev.map((x, j) =>
                                j === i ? { ...x, name: e.target.value } : x
                              )
                            )
                          }
                        />
                      </div>
                      <div className='w-full shrink-0 space-y-1 md:w-[180px]'>
                        <FieldLabel className={restyledFieldLabelClass}>
                          {t('form.certificationValidUntil')}
                        </FieldLabel>
                        <DatePicker
                          value={c.valid_until ?? ''}
                          onChange={next =>
                            setCertifications(prev =>
                              prev.map((x, j) =>
                                j === i ? { ...x, valid_until: next } : x
                              )
                            )
                          }
                          className={restyledDatePickerClass}
                        />
                      </div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className={deleteBtnClass}
                        onClick={() =>
                          setCertifications(prev =>
                            prev.filter((_, j) => j !== i)
                          )
                        }
                      >
                        <X className='h-4 w-4' />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type='button'
                    variant='outline'
                    className={addNewBtnClass}
                    onClick={() =>
                      setCertifications(prev => [
                        ...prev,
                        { name: '', valid_until: '' },
                      ])
                    }
                  >
                    <Plus className='mr-2 h-4 w-4' />{' '}
                    {t('form.addCertification')}
                  </Button>
                </div>
              </IdentifySection>

              <IdentifySection
                title={t('form.skillsCommission')}
                titleClassName={restyledSectionTitleClass}
              >
                <div className='space-y-3'>
                  {availableSkillNames.length === 0 && (
                    <p className='text-sm text-muted-foreground'>
                      {t('list.filters.noSkillOptions')}
                    </p>
                  )}

                  {availableSkillNames.map(skillName => {
                    const selectedSkill = skills.find(
                      skill => skill.name === skillName
                    )
                    const selected = !!selectedSkill

                    return (
                      <div
                        key={skillName}
                        className={cn(
                          'flex items-center gap-3 rounded-2xl border px-3 py-3 transition-colors',
                          selected
                            ? 'border-[#6ee7b7] bg-[#ecfdf3]'
                            : 'border-[#e7e9ef] bg-white'
                        )}
                      >
                        <button
                          type='button'
                          className={cn(
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                            selected
                              ? 'border-[#22c55e] bg-[#22c55e] text-white'
                              : 'border-[#d6dbe5] bg-white text-transparent'
                          )}
                          onClick={() => toggleSkill(skillName)}
                          aria-pressed={selected}
                          aria-label={skillName}
                        >
                          <Check className='h-3 w-3' />
                        </button>
                        <button
                          type='button'
                          className='min-w-0 flex-1 text-left text-sm font-medium leading-[1.4] text-[#171a20]'
                          onClick={() => toggleSkill(skillName)}
                        >
                          {skillName}
                        </button>
                        <div className='flex items-center gap-2'>
                          <Input
                            value={selectedSkill?.commission ?? ''}
                            type='number'
                            inputMode='decimal'
                            step='0.01'
                            placeholder='5'
                            disabled={!selected}
                            className={cn(
                              'h-8 w-20 rounded-xl border-transparent bg-white px-3 py-1 text-center text-sm font-normal leading-[1.4] disabled:cursor-not-allowed disabled:bg-[#f8f9fb] disabled:text-[#b0b5c3]',
                              FORM_CONTROL_FOCUS_CLASS
                            )}
                            onChange={e =>
                              updateSkillCommission(skillName, e.target.value)
                            }
                          />
                          <span className='text-sm font-normal leading-[1.4] text-[#8f95a6]'>
                            %
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </IdentifySection>
            </>
          )}

          {step === 2 && (
            <>
              <IdentifySection
                title={t('form.workingDaysSection')}
                titleClassName={restyledSectionTitleClass}
              >
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <div className='flex flex-wrap gap-2'>
                      {TECHNICIAN_WEEK_DAYS_UI.map(day => {
                        const selected = workingDays.includes(day)
                        return (
                          <button
                            key={day}
                            type='button'
                            className={cn(
                              'rounded-xl border px-4 py-2 text-xs font-normal leading-4 transition-colors',
                              selected
                                ? 'border-[#6ee7b7] bg-[#ecfdf3] text-[#171a20]'
                                : 'border-[#e5e7ee] bg-white text-[#4b5565]'
                            )}
                            onClick={() => toggleDay(day)}
                          >
                            {weekdayLabels[day]}
                          </button>
                        )
                      })}
                    </div>
                    {errors.working_days && (
                      <p className='text-xs text-destructive'>
                        {errors.working_days}
                      </p>
                    )}
                  </div>

                  <button
                    type='button'
                    className={cn(
                      'rounded-xl border px-4 py-2 text-xs font-normal leading-4 transition-colors',
                      worksWeekends
                        ? 'border-[#6ee7b7] bg-[#ecfdf3] text-[#171a20]'
                        : 'border-[#e5e7ee] bg-white text-[#4b5565]'
                    )}
                    onClick={() => setWorksWeekends(prev => !prev)}
                  >
                    {t('form.worksWeekends')}
                  </button>

                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-1'>
                      <RequiredLabel
                        htmlFor='start_time'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.startTime')}
                      </RequiredLabel>
                      <Input
                        id='start_time'
                        type='time'
                        value={startTime}
                        className={cn(
                          restyledFieldInputClass,
                          errors.start_time && 'border-destructive'
                        )}
                        onChange={e => setStartTime(e.target.value)}
                      />
                      {errors.start_time && (
                        <p className='text-xs text-destructive'>
                          {errors.start_time}
                        </p>
                      )}
                    </div>
                    <div className='space-y-1'>
                      <RequiredLabel
                        htmlFor='end_time'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.endTime')}
                      </RequiredLabel>
                      <Input
                        id='end_time'
                        type='time'
                        value={endTime}
                        className={cn(
                          restyledFieldInputClass,
                          errors.end_time && 'border-destructive'
                        )}
                        onChange={e => setEndTime(e.target.value)}
                      />
                      {errors.end_time && (
                        <p className='text-xs text-destructive'>
                          {errors.end_time}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='grid gap-4 sm:grid-cols-2'>
                    <div className='space-y-1'>
                      <FieldLabel
                        htmlFor='break_duration'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.lunchBreak')}
                      </FieldLabel>
                      <select
                        id='break_duration'
                        value={breakDurationValue}
                        className={restyledNativeSelectClass}
                        onChange={e => onBreakDurationChange(e.target.value)}
                      >
                        {breakDurationOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.break_start && (
                        <p className='text-xs text-destructive'>
                          {errors.break_start}
                        </p>
                      )}
                      {errors.break_end && (
                        <p className='text-xs text-destructive'>
                          {errors.break_end}
                        </p>
                      )}
                    </div>

                    <div className='space-y-1'>
                      <FieldLabel
                        htmlFor='leave_status'
                        className={restyledFieldLabelClass}
                      >
                        {t('form.currentlyOnLeave')}
                      </FieldLabel>
                      <select
                        id='leave_status'
                        value={onLeave ? 'yes' : 'no'}
                        className={cn(restyledNativeSelectClass, 'capitalize')}
                        onChange={e => {
                          const nextOnLeave = e.target.value === 'yes'
                          setOnLeave(nextOnLeave)
                          if (!nextOnLeave) {
                            setLeaveFrom('')
                            setLeaveTo('')
                          }
                        }}
                      >
                        <option value='no'>{t('no')}</option>
                        <option value='yes'>{t('yes')}</option>
                      </select>
                    </div>
                  </div>

                  {onLeave && (
                    <div className='grid gap-4 sm:grid-cols-2'>
                      <div className='space-y-1'>
                        <RequiredLabel
                          htmlFor='leave_from'
                          className={restyledFieldLabelClass}
                        >
                          {t('form.from')}
                        </RequiredLabel>
                        <DatePicker
                          id='leave_from'
                          value={leaveFrom}
                          onChange={setLeaveFrom}
                          className={restyledDatePickerClass}
                          error={!!errors.leave_from}
                          max={leaveTo || undefined}
                        />
                        {errors.leave_from && (
                          <p className='text-xs text-destructive'>
                            {errors.leave_from}
                          </p>
                        )}
                      </div>
                      <div className='space-y-1'>
                        <RequiredLabel
                          htmlFor='leave_to'
                          className={restyledFieldLabelClass}
                        >
                          {t('form.to')}
                        </RequiredLabel>
                        <DatePicker
                          id='leave_to'
                          value={leaveTo}
                          onChange={setLeaveTo}
                          className={restyledDatePickerClass}
                          error={!!errors.leave_to}
                          min={leaveFrom || undefined}
                        />
                        {errors.leave_to && (
                          <p className='text-xs text-destructive'>
                            {errors.leave_to}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </IdentifySection>

              <IdentifySection
                title={t('form.vehiclesSection')}
                titleClassName={restyledSectionTitleClass}
              >
                <div className='grid gap-4 sm:grid-cols-2'>
                  {vehicles.map((vehicle, index) => (
                    <div key={index} className='contents'>
                      <div className='space-y-1'>
                        <FieldLabel
                          htmlFor={`vehicle_name_${index}`}
                          className={restyledFieldLabelClass}
                        >
                          {t('form.vehicleRowLabel', { index: index + 1 })}
                        </FieldLabel>
                        <Input
                          id={`vehicle_name_${index}`}
                          value={vehicle.name}
                          maxLength={MAX_VEHICLE_NAME}
                          placeholder={t('clients:form.enterHere')}
                          className={restyledFieldInputClass}
                          onChange={e =>
                            updateVehicle(index, 'name', e.target.value)
                          }
                        />
                      </div>
                      <div className='space-y-1'>
                        <FieldLabel
                          htmlFor={`vehicle_plate_${index}`}
                          className={restyledFieldLabelClass}
                        >
                          {t('form.registrationLabel', { index: index + 1 })}
                        </FieldLabel>
                        <Input
                          id={`vehicle_plate_${index}`}
                          value={vehicle.plate}
                          maxLength={32}
                          placeholder={t('clients:form.enterHere')}
                          className={restyledFieldInputClass}
                          onChange={e =>
                            updateVehicle(index, 'plate', e.target.value)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </IdentifySection>

              <section className='space-y-4'>
                <div className='rounded-2xl border border-[#e7e9ef] bg-white p-4 shadow-sm'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0 font-heading text-sm font-semibold leading-[1.2] text-[#3d434e]'>
                      {t('form.vehicleEquipmentSection')}
                    </div>
                    <button
                      type='button'
                      className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8] text-[#6c7285] transition-colors hover:bg-[#eceef4]'
                      aria-expanded={isVehicleEquipmentExpanded}
                      aria-controls='vehicle-equipment-panel'
                      onClick={() =>
                        setIsVehicleEquipmentExpanded(prev => !prev)
                      }
                    >
                      {isVehicleEquipmentExpanded ? (
                        <ChevronUp className='h-4 w-4' />
                      ) : (
                        <ChevronDown className='h-4 w-4' />
                      )}
                    </button>
                  </div>

                  {isVehicleEquipmentExpanded && (
                    <div
                      id='vehicle-equipment-panel'
                      className='mt-4 space-y-4'
                    >
                      <div className='flex flex-wrap gap-2'>
                        {EQUIPMENT_PRESETS.map(item => {
                          const selected = equipment.includes(item)
                          return (
                            <button
                              key={item}
                              type='button'
                              className={cn(
                                'rounded-xl border px-4 py-2 text-xs font-normal leading-4 transition-colors',
                                selected
                                  ? 'border-[#10b981] bg-[#ecfdf5] text-[#171a20]'
                                  : 'border-[#e5e7ee] bg-white text-[#171a20]'
                              )}
                              onClick={() => toggleEquipment(item)}
                            >
                              {item}
                            </button>
                          )
                        })}
                        {customEquipment.map(item => (
                          <button
                            key={item}
                            type='button'
                            className='rounded-xl border border-[#10b981] bg-[#ecfdf5] px-4 py-2 text-xs font-normal leading-4 text-[#171a20]'
                            onClick={() => toggleEquipment(item)}
                          >
                            {item}
                          </button>
                        ))}
                        <button
                          type='button'
                          className='rounded-xl border border-dashed border-[#ff8a61] px-4 py-2 text-xs font-medium leading-4 text-primary'
                          onClick={() =>
                            document.getElementById('equipment_input')?.focus()
                          }
                        >
                          + {t('form.other')}
                        </button>
                      </div>

                      <div className='flex items-center gap-3'>
                        <Input
                          id='equipment_input'
                          value={equipmentInput}
                          maxLength={MAX_EQUIPMENT_NAME}
                          placeholder={t('form.equipmentPlaceholder')}
                          className={cn(restyledFieldInputClass, 'flex-1')}
                          onChange={e => setEquipmentInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              addCustomEquipment()
                            }
                          }}
                        />
                        <button
                          type='button'
                          className='text-sm font-medium leading-none text-primary disabled:cursor-not-allowed disabled:opacity-50'
                          onClick={addCustomEquipment}
                          disabled={equipment.length >= MAX_EQUIPMENT}
                        >
                          + {t('form.add')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {/* ---------- STEP 3: Intervention Zone + Documents ---------- */}
          {step === 3 && (
            <>
              <section className='space-y-6 rounded-[28px] border border-[#e7e9ef] bg-white p-6 shadow-[0_1px_3px_rgba(23,26,32,0.04)]'>
                <div className='space-y-1'>
                  <RequiredLabel
                    htmlFor='intervention_radius'
                    className={restyledFieldLabelClass}
                  >
                    {t('form.interventionRadius')}
                  </RequiredLabel>
                  <Input
                    id='intervention_radius'
                    type='number'
                    min={0}
                    value={interventionRadius}
                    placeholder={t('form.interventionRadiusPlaceholder')}
                    className={cn(
                      restyledFieldInputClass,
                      errors.intervention_radius && 'border-destructive'
                    )}
                    onChange={e =>
                      setInterventionRadius(
                        e.target.value.slice(0, MAX_RADIUS_DIGITS)
                      )
                    }
                  />
                  {errors.intervention_radius && (
                    <p className='text-xs text-destructive'>
                      {errors.intervention_radius}
                    </p>
                  )}
                </div>

                <div className='space-y-4'>
                  <AccentSectionLabel
                    title={t('detail.areasCovered')}
                    className={restyledSectionTitleClass}
                  />

                  <div className='space-y-4'>
                    {zones.length > 0 && (
                      <div className='flex flex-wrap gap-2'>
                        {zones.map(zone => (
                          <span
                            key={zone}
                            className='inline-flex items-center gap-2 rounded-xl border border-[#10B981] bg-[#ECFDF5] px-3 py-2 text-xs font-medium leading-4 text-[#171a20]'
                          >
                            <span className='truncate leading-4'>{zone}</span>
                            <button
                              type='button'
                              className='inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-[#e5e7eb] bg-[#f8fafc] text-[#374151] transition-colors hover:bg-white hover:text-[#171a20]'
                              onClick={() => removeZone(zone)}
                              aria-label={t('form.removeZone', { zone })}
                            >
                              <X className='h-3.5 w-3.5' />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {isAddingZone ? (
                      <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                        <Input
                          id='zone_input'
                          value={zoneInput}
                          autoFocus
                          maxLength={MAX_ZONE_LENGTH}
                          placeholder={t('form.zonePlaceholder')}
                          className={cn(restyledFieldInputClass, 'sm:max-w-xs')}
                          onChange={e => setZoneInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              addZone()
                            }

                            if (e.key === 'Escape') {
                              setZoneInput('')
                              setIsAddingZone(false)
                            }
                          }}
                        />

                        <div className='flex gap-2'>
                          <Button
                            type='button'
                            className='rounded-xl bg-primary px-4 py-2 text-sm font-medium leading-none text-white hover:bg-primary/90'
                            onClick={addZone}
                          >
                            {t('form.addZone')}
                          </Button>
                          <Button
                            type='button'
                            variant='outline'
                            className='rounded-xl border-[#e7e9ef] px-4 py-2 text-sm font-medium leading-none text-[#3d434e] hover:bg-[#f8f9fb]'
                            onClick={() => {
                              setZoneInput('')
                              setIsAddingZone(false)
                            }}
                          >
                            {t('form.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        type='button'
                        variant='outline'
                        className='w-fit rounded-xl border border-dashed border-primary px-4 py-2 text-xs font-medium leading-none text-primary hover:bg-[#fff5f1]'
                        onClick={() => setIsAddingZone(true)}
                      >
                        <Plus className='mr-2 h-4 w-4' />
                        {t('form.addArea')}
                      </Button>
                    )}
                  </div>
                </div>

                <div className='space-y-4'>
                  <AccentSectionLabel
                    title={t('form.documents')}
                    className={restyledSectionTitleClass}
                  />

                  <input
                    ref={documentInputRef}
                    type='file'
                    multiple
                    accept={DOCUMENT_FILE_ACCEPT}
                    className='hidden'
                    onChange={event => {
                      queueDocumentFiles(event.target.files)
                      event.target.value = ''
                    }}
                  />

                  <div
                    role='button'
                    tabIndex={0}
                    onClick={() => documentInputRef.current?.click()}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        documentInputRef.current?.click()
                      }
                    }}
                    onDragOver={event => {
                      event.preventDefault()
                      setIsDocumentDragActive(true)
                    }}
                    onDragLeave={event => {
                      event.preventDefault()
                      setIsDocumentDragActive(false)
                    }}
                    onDrop={event => {
                      event.preventDefault()
                      setIsDocumentDragActive(false)
                      queueDocumentFiles(event.dataTransfer.files)
                    }}
                    className={cn(
                      'rounded-xl border border-dashed bg-[#f4f5f8] p-4 transition-colors',
                      isDocumentDragActive
                        ? 'border-primary bg-[#fff5f1]'
                        : 'border-[#e7e9ef] hover:bg-[#eef1f5]'
                    )}
                  >
                    <div className='flex flex-col items-start gap-4 sm:flex-row sm:items-center'>
                      <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-[#272c36]'>
                        <Upload className='h-4 w-4' />
                      </div>
                      <div className='text-left text-xs font-normal leading-4 text-[#3d434e]'>
                        <p>{t('shared:attachFiles.uploadHintPrimary')}</p>
                        <p>{t('form.documentsFormatHint')}</p>
                      </div>
                    </div>
                  </div>

                  <AttachmentPreviewGrid
                    items={attachmentPreviews}
                    onRemove={item => {
                      void removeAttachment(item)
                    }}
                    onRename={renameAttachment}
                    removeLabel={t('shared:attachFiles.remove')}
                  />
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <div className='tf-right-drawer-footer sticky bottom-0 z-20 justify-between'>
        <div className='flex w-full items-center justify-between gap-4'>
          <Button
            type='button'
            variant='outline'
            className={cancelBtnClass}
            onClick={closeForm}
            disabled={pending}
          >
            {t('form.cancel')}
          </Button>

          <div className='flex items-center gap-3'>
            {step > 1 && (
              <Button
                type='button'
                variant='outline'
                className={outlineBtnClass}
                onClick={goBack}
                disabled={pending}
              >
                <ArrowLeft className='mr-2 h-4 w-4' />
                {t('form.back')}
              </Button>
            )}
            <Button
              type='button'
              className={primaryBtnClass}
              onClick={goNext}
              disabled={pending}
            >
              {step === FORM_STEPS ? (
                isEdit ? (
                  t('form.saveChanges')
                ) : (
                  t('form.save')
                )
              ) : (
                <>
                  {t('form.next')}
                  <ArrowRight className='ml-2 h-4 w-4' />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// 2.10.3 / 2.10.4 — route-backed create/edit drawer. The list or detail page
// stays mounted behind the drawer so deep links still open in the redesigned
// slider flow from the plan.
export default function TechnicianFormPage({
  mode,
}: {
  mode: TechnicianFormMode
}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const closeRoute = () =>
    navigate(mode === 'edit' && id ? `/technicians/${id}` : '/technicians')

  return (
    <>
      {mode === 'edit' ? <TechnicianDetailPage /> : <TechniciansListPage />}

      <TechnicianFormDrawerShell open onClose={closeRoute}>
        <TechnicianFormContent
          mode={mode}
          onCancel={closeRoute}
          onSuccess={technicianId => navigate(`/technicians/${technicianId}`)}
        />
      </TechnicianFormDrawerShell>
    </>
  )
}
